import { HttpService } from "../modules/shared/services/http.service";
import { ContentManagementTypeEnum } from "../modules/shared/model/ContentManagementInterfaces";

export interface TranslatedContent {
  title: string;
  content: string;
  isTranslated: boolean;
}

export interface TranslatedSection {
  sectionTitle: string;
  sectionContent: string;
  isTranslated: boolean;
}

export interface TranslatedContentWithSections {
  title: string;
  sections: TranslatedSection[];
  hasAnyTranslation: boolean;
}

export class ContentTranslationService {
  /**
   * Get content with ALL sections and their translations for a specific language
   */
  static async getContentWithSections(
    contentType: ContentManagementTypeEnum,
    languageId: string
  ): Promise<TranslatedContentWithSections | null> {
    try {
      console.log(`[Translation] Fetching ALL sections for type: ${contentType}, language: ${languageId}`);
      
      // 1. Get ALL content sections by type (there can be multiple sections)
      const contentResponse = await HttpService.get(
        `api/v1/content-managements/type/${contentType}`,
        true,
        {}
      );

      if (!contentResponse || !contentResponse.data || contentResponse.data.length === 0) {
        console.log(`[Translation] No content sections found for type: ${contentType}`);
        return null;
      }

      const allSections = contentResponse.data;
      console.log(`[Translation] Found ${allSections.length} sections`);

      // 2. For each section, try to get its translation
      const sectionsWithTranslations: TranslatedSection[] = [];
      let hasAnyTranslation = false;

      for (const section of allSections) {
        console.log(`[Translation] Processing section: "${section.contentTitle}"`);
        
        // Get translations for this specific section
        const translationsResponse = await HttpService.get(
          `api/v1/content-management-translations/content-management/${section.id}`,
          true,
          {}
        );

        let sectionTitle = section.contentTitle;
        let sectionContent = section.contentDescription;
        let isTranslated = false;

        // Check if translation exists for current language
        if (translationsResponse && Array.isArray(translationsResponse)) {
          const translation = translationsResponse.find(
            (t: any) => t.language?.id === languageId
          );

          if (translation) {
            console.log(`[Translation] ✅ Translation found for section "${section.contentTitle}"`);
            sectionTitle = translation.title || section.contentTitle;
            sectionContent = translation.content || section.contentDescription;
            isTranslated = true;
            hasAnyTranslation = true;
          } else {
            console.log(`[Translation] ⚠️ No translation for section "${section.contentTitle}", using original`);
          }
        }

        sectionsWithTranslations.push({
          sectionTitle,
          sectionContent,
          isTranslated,
        });
      }

      // 3. Determine overall title (use first section's title)
      const overallTitle = sectionsWithTranslations[0]?.sectionTitle || "Content";

      return {
        title: overallTitle,
        sections: sectionsWithTranslations,
        hasAnyTranslation,
      };
    } catch (error) {
      console.error("[Translation] Error fetching content with sections:", error);
      return null;
    }
  }

  /**
   * Get content with translation for a specific language (legacy - single section)
   */
  static async getContentWithTranslation(
    contentType: ContentManagementTypeEnum,
    languageId: string
  ): Promise<TranslatedContent | null> {
    try {
      const result = await this.getContentWithSections(contentType, languageId);
      
      if (!result) {
        return null;
      }

      // Combine all sections into single content with HTML structure
      const combinedContent = result.sections
        .map(section => {
          return `
            <div class="content-section mb-6">
              <h3 class="text-lg font-semibold mb-3">${section.sectionTitle}</h3>
              <div class="section-content">${section.sectionContent}</div>
            </div>
          `;
        })
        .join("\n");

      return {
        title: result.title,
        content: combinedContent,
        isTranslated: result.hasAnyTranslation,
      };
    } catch (error) {
      console.error("[Translation] Error fetching content with translation:", error);
      return null;
    }
  }

  /**
   * Get page with translation for a specific language
   */
  static async getPageWithTranslation(
    pageUrl: string,
    languageId: string
  ): Promise<TranslatedContent | null> {
    try {
      // Normalize URL
      const normalizedUrl = pageUrl.startsWith("/") ? pageUrl : `/${pageUrl}`;
      
      // Get page by URL
      const response = await HttpService.get(
        `api/v1/page-managements?filters=${encodeURIComponent(JSON.stringify({ url: normalizedUrl }))}`,
        true,
        {}
      );

      if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const page = response.data[0];
        
        // TODO: Add page management translations when backend is ready
        // For now, return the page content as is
        return {
          title: page.title,
          content: page.content,
          isTranslated: false,
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching page with translation:", error);
      return null;
    }
  }

  /**
   * Detect content type from URL or title
   */
  static detectContentType(url: string, title: string): ContentManagementTypeEnum | null {
    const lowerUrl = (url || "").toLowerCase();
    const lowerTitle = (title || "").toLowerCase();

    console.log(`[Translation] Detecting content type for URL: "${url}", Title: "${title}"`);

    // Check for professional terms first (more specific)
    if (lowerUrl.includes("professional") && lowerUrl.includes("terms")) {
      console.log("[Translation] Detected: Professional_Terms");
      return ContentManagementTypeEnum.Professional_Terms;
    }
    if (lowerTitle.includes("professional") && lowerTitle.includes("terms")) {
      console.log("[Translation] Detected: Professional_Terms");
      return ContentManagementTypeEnum.Professional_Terms;
    }

    // Then check for regular terms
    if (lowerUrl.includes("terms") || lowerUrl.includes("condition")) {
      console.log("[Translation] Detected: Terms_Conditions");
      return ContentManagementTypeEnum.Terms_Conditions;
    }
    if (lowerTitle.includes("terms") || lowerTitle.includes("condition")) {
      console.log("[Translation] Detected: Terms_Conditions");
      return ContentManagementTypeEnum.Terms_Conditions;
    }

    // Privacy policy
    if (lowerUrl.includes("privacy") || lowerUrl.includes("policy")) {
      console.log("[Translation] Detected: Privacy_Policy");
      return ContentManagementTypeEnum.Privacy_Policy;
    }
    if (lowerTitle.includes("privacy") || lowerTitle.includes("policy")) {
      console.log("[Translation] Detected: Privacy_Policy");
      return ContentManagementTypeEnum.Privacy_Policy;
    }

    // Emergency services
    if (lowerUrl.includes("emergency") || lowerTitle.includes("emergency")) {
      console.log("[Translation] Detected: Emergency_Services");
      return ContentManagementTypeEnum.Emergency_Services;
    }

    // Customer information
    if (lowerUrl.includes("customer") || lowerTitle.includes("customer")) {
      console.log("[Translation] Detected: Customer_Information");
      return ContentManagementTypeEnum.Customer_Information;
    }

    // Professional information
    if (lowerUrl.includes("professional-info") || lowerTitle.includes("professional information")) {
      console.log("[Translation] Detected: Professional_Information");
      return ContentManagementTypeEnum.Professional_Information;
    }

    // Contact information
    if (lowerUrl.includes("contact") || lowerTitle.includes("contact")) {
      console.log("[Translation] Detected: Contact_Information");
      return ContentManagementTypeEnum.Contact_Information;
    }

    console.log("[Translation] ⚠️ No content type detected");
    return null;
  }
}

